import React from "react";
import "./Radio.scss";

export interface RadioOption {
  value: string;
  label?: React.ReactNode;
  checkedChildren?: React.ReactNode;
  disabled?: boolean;
}

export interface RadioProps {
  options: RadioOption[];
  value: string;
  size: "md" | "lg";
  isBordered?: boolean;
  isInline?: boolean;
  onChange: (value: string) => void;
  getOptionTestId?: (option: RadioOption) => string | undefined;
}

export default function Radio({
  options,
  value,
  size = "md",
  isBordered = false,
  isInline = false,
  onChange,
  getOptionTestId,
}: RadioProps) {
  const select = (optionValue: string) => {
    if (value !== optionValue) {
      onChange(optionValue);
    }
  };

  return (
    <div className={`radio-group ${size}${isInline ? " inline" : ""}`} role="radiogroup">
      {options.map((option) => {
        const isSelected = value === option.value;
        const isDisabled = option.disabled;

        return (
          <div
            key={option.value}
            role="radio"
            aria-checked={isSelected}
            tabIndex={isDisabled ? -1 : 0}
            data-testid={getOptionTestId?.(option)}
            className={`radio-option
              ${isSelected ? "selected" : ""}
              ${isDisabled ? "disabled" : ""}
              ${isBordered ? "border" : ""}
            `}
            onClick={() => !isDisabled && select(option.value)}
            onKeyDown={(e) => {
              if (isDisabled) return;

              if (e.key === " " || e.key === "Enter") {
                e.preventDefault();
                select(option.value);
              }
            }}
          >
            <div className="radio-custom">
              <div className="radio-inner" />
            </div>

            <div className="radio-content">
              {option.label && <div className="radio-label">{option.label}</div>}

              {option.checkedChildren && isSelected && <div className="radio-children">{option.checkedChildren}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

Radio.defaultProps = {
  isBordered: false,
};
